import { ChangeDetectionStrategy, Component, DestroyRef, HostListener, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { CdkDrag, CdkDragEnd, CdkDragHandle, Point } from '@angular/cdk/drag-drop';

import { NvButton } from '../../shared/ui/button/nv-button';
import { NvIcon } from '../../shared/ui/icon/nv-icon';
import { NvTab, NvTabBar } from '../../shared/ui/nav/nv-tab-bar';
import { RequestAlerts } from '../../shared/components/request-alerts/request-alerts';
import { OrderApprovalStrip } from '../../shared/components/order-approval-strip/order-approval-strip';
import { PendingRequestsService } from '../../core/notifications/pending-requests.service';
import { OrdersStore } from '../../core/stores/orders.store';
import { AlarmService } from '../../core/notifications/alarm.service';
import { TenancyService } from '../../core/tenancy/tenancy.service';
import { TasksStore } from '../../core/stores/tasks.store';
import { VenueTask } from '../../shared/models/task.model';
import { toMillis } from '../../shared/utils/timestamp.util';
import { RouterLink } from '@angular/router';

type DockTab = 'requests' | 'orders' | 'tasks';

/** Matches the mock's own clamp on reqH — never shorter than this. */
const MIN_HEIGHT_PX = 180;
const DEFAULT_HEIGHT_PX = 420;
/** Gap between the card's resting position and the bottom-right corner — mirrors the pill's own `right/bottom` in requests-tasks-dock.scss, including its narrow-viewport override, because the card's top anchor (see 1c) has to reproduce that resting spot arithmetically now that CSS no longer sets it. */
const BOTTOM_INSET_PX = 24;
const BOTTOM_INSET_NARROW_PX = 12;
const NARROW_MAX_WIDTH_PX = 599;
/**
 * Round 4, 1c — deliberately smaller than BOTTOM_INSET_PX, and used only
 * while a resize is actually in progress. `topPx` seeds the card so its
 * *resting* bottom edge already sits exactly BOTTOM_INSET_PX above the
 * viewport floor — a live check found that reusing that same, more
 * generous margin as the resize ceiling made the resting height read as
 * "already at the maximum", so dragging the grip down could never grow the
 * card at all (only shrink it) on any screen size: `topPx` is defined as
 * `innerHeight − inset − height`, so `innerHeight − inset − topPx` is
 * `height` by construction, whatever `inset` is plugged back in. Giving
 * resize its own, tighter margin breaks that identity on purpose, so the
 * grip has real room to grow the card into before it touches the edge.
 */
const RESIZE_BOTTOM_MARGIN_PX = 8;
/** The mock's own threshold for opening as a full card by default (mock: `reqOpen: window.innerWidth >= 1440 && window.innerHeight >= 700`) — a laptop or phone gets the collapsed pill instead, where a 344×420 card would eat a third of the screen. */
const DEFAULT_OPEN_MIN_WIDTH = 1440;
const DEFAULT_OPEN_MIN_HEIGHT = 700;
/** When several tabs gain something in the same tick, the newest arrival is shown in this order of urgency. */
const ARRIVAL_PRIORITY: readonly DockTab[] = ['orders', 'requests', 'tasks'];

/**
 * Item 15 — the mock's floating, draggable, resizable, tabbed "Requests &
 * tasks" dock. Replaces the two components that used to sit in page flow
 * above the router outlet (shell.html's own comment on why: no drag support
 * existed yet, so a fixed-position overlay risked sitting on top of a real
 * control and eating its click — that gap is what this component closes).
 *
 * The alerting/escalation engines themselves are NOT rewritten here —
 * `RequestAlerts` (Requests tab) and `OrderApprovalStrip` (Tasks tab, per
 * the original shell-migration plan's own intent) are mounted as real child
 * components and keep every bit of their round/kick/loop/speech logic
 * exactly as it was; only their own header chrome moved up into this
 * shell (see each component's own template note). Both stay mounted at all
 * times — hidden via [hidden], never @if — switching tabs must not tear
 * down and restart their timers.
 *
 * Round 3 fix — this used to default to the collapsed pill on every screen
 * size, which (combined with a round-2 CSS bug that made the pill itself
 * invisible) meant the dock was effectively never on screen. The CSS bug is
 * gone, and the default state now matches the mock's own rule — open by
 * default on a desktop-sized viewport, collapsed pill below it.
 *
 * Item 27 — the timed auto-minimise is GONE, by explicit client decision:
 * once the dock surfaces itself for a new arrival it stays open until the
 * manager minimises it themselves (chevron, Escape, or the pill). The
 * auto-OPEN on arrival is kept; only the 10s close timer was removed.
 *
 * Know the tradeoff before "fixing" this back: that timer was not
 * decoration. With no timeout at all, `e2e/manager/staff-and-payroll.spec.ts`
 * MGR-03/MGR-04 failed with Playwright's actionability log reading
 * "<nv-requests-tasks-dock>... subtree intercepts pointer events" against
 * real buttons ("Request overtime…", "Run payroll") nowhere near any table
 * the dock cares about — i.e. a permanently-open dock can sit on top of and
 * block unrelated controls. If those specs regress, the fix is to move or
 * shrink the dock (or have the specs minimise it first), NOT to reinstate a
 * timer the client asked for the removal of.
 *
 * Round 4 — two corrections, both to the drag/resize interaction:
 *
 *  - The pill (and the card's own header, mute and minimise buttons) are all
 *    `cdkDragHandle`/`cdkDrag` regions AND ordinary click targets at once.
 *    CDK ignores pointer movement under its own 5px drag threshold but has
 *    no click suppression at all, so a real drag still ends in a synthesised
 *    `click` on mouseup — dragging the pill re-opened the card wherever it
 *    was dropped. `*Dragged` flags (reset on `pointerdown`, set on
 *    `cdkDragStarted`, which CDK only ever emits once the threshold is
 *    actually crossed) now gate every click in a drag-handle region.
 *  - The card used to anchor by its `bottom` edge (see the SCSS file's own
 *    note on why `all: unset` made that fragile), so growing `heightPx` via
 *    the resize grip pushed the *top* edge up — the grip itself, and
 *    everything below it, never moved, which read as the card resizing
 *    from the top while the handle sat at the bottom. `topPx` now anchors
 *    the card instead: `startResize` only ever mutates `heightPx`, so the
 *    bottom edge (top + height) tracks the pointer and the top stays where
 *    it is. `topPx` is still kept pinned to the bottom-right corner on a
 *    plain window resize — but only until the manager has actually dragged
 *    or resized the card themselves (`topPinned`), so a deliberate
 *    reposition is never yanked back.
 */
@Component({
  selector: 'nv-requests-tasks-dock',
  imports: [CdkDrag, CdkDragHandle, NvButton, NvIcon, NvTabBar, RequestAlerts, OrderApprovalStrip, RouterLink],
  templateUrl: './requests-tasks-dock.html',
  styleUrl: './requests-tasks-dock.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestsTasksDock {
  private readonly pendingRequests = inject(PendingRequestsService);
  private readonly ordersStore = inject(OrdersStore);
  private readonly alarm = inject(AlarmService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly tenancy = inject(TenancyService);
  private readonly tasksStore = inject(TasksStore);

  readonly audioBlocked = this.alarm.blocked;

  /** Orders (and therefore order-approval "tasks") are preview-gated — mirrors shell.html's own old `@if (previewFeatures)` around <nv-order-approval-strip>, so a venue without that preview flag never even mounts OrderApprovalStrip, not just hides its tab. */
  readonly showTasksTab = input(false);

  readonly requestsCount = this.pendingRequests.count;
  /** Guest orders waiting for approval — its own Orders tab (was the Tasks tab's content). */
  readonly ordersCount = computed(() => (this.showTasksTab() ? this.ordersStore.pendingApproval().length : 0));

  /** Staff tasks not yet picked up, oldest first (FIFO, like the other two tabs). */
  readonly pendingTasks = computed(() =>
    this.tasksStore
      .tasks()
      .filter((t) => t.status === 'pending')
      .sort((a, b) => (toMillis(a.createdAt) ?? 0) - (toMillis(b.createdAt) ?? 0))
  );
  readonly tasksCount = computed(() => this.pendingTasks().length);
  readonly totalCount = computed(() => this.requestsCount() + this.ordersCount() + this.tasksCount());

  readonly tabs = computed<NvTab<DockTab>[]>(() => {
    const tab = (value: DockTab, label: string, count: number): NvTab<DockTab> => ({
      value,
      label,
      badge: count > 0 ? count : null,
      alert: count > 0,
    });
    const tabs: NvTab<DockTab>[] = [tab('requests', 'Requests', this.requestsCount())];
    if (this.showTasksTab()) tabs.push(tab('orders', 'Orders', this.ordersCount()));
    tabs.push(tab('tasks', 'Tasks', this.tasksCount()));
    return tabs;
  });

  taskDue(task: VenueTask): string {
    return new Date(task.dueAt).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
  }

  /** Mock: `reqOpen: window.innerWidth >= 1440 && window.innerHeight >= 700` — see DEFAULT_OPEN_MIN_WIDTH/HEIGHT's own note. `window` guarded for SSR/test harnesses that construct this component without one. */
  readonly open = signal(this.defaultOpen());
  readonly activeTab = signal<DockTab>('requests');

  readonly heightPx = signal(DEFAULT_HEIGHT_PX);
  /**
   * Round 4, 1c — the card's own top edge, replacing a CSS `bottom: 24px`
   * anchor that made the resize grip grow the card upward (see the class
   * doc comment). Seeded to reproduce the exact same resting position
   * (bottom-right, 24px/12px inset) so nothing visually moves at rest;
   * `startResize` never touches this, only `heightPx` — that's what makes
   * the bottom edge the one that tracks the pointer.
   */
  readonly topPx = signal(this.restingTopPx(DEFAULT_HEIGHT_PX));
  /** CDK's own free-drag offset — {0,0} is "wherever the CSS anchors it", not the viewport origin. Card and the collapsed pill are dragged independently, so each gets its own. */
  readonly cardPosition = signal<Point>({ x: 0, y: 0 });
  readonly pillPosition = signal<Point>({ x: 0, y: 0 });

  /**
   * Arrival tracking is by item id, not by raw count. The old count-increase
   * check treated each listener's *initial* snapshot (0 -> N, landing a
   * beat after the shell renders) as a new arrival, so a manager who
   * minimised the dock in their first second on a page had it pop straight
   * back open with nothing new — and a resolve + arrival in the same tick
   * (count unchanged) never surfaced it at all. `seenIds` holds every
   * `tab:id` this dock has already accounted for; `baselined` records which
   * tabs have delivered their first snapshot for `arrivalsLicenseId`.
   */
  private readonly seenIds = new Set<string>();
  private readonly baselined = new Set<DockTab>();
  private arrivalsLicenseId: string | null = null;
  /** Set once the manager minimises the dock; from then on only genuinely new items (never the initial backlog) may re-open it. */
  private dismissedByUser = false;
  /** True only while the card is open because it opened itself (on load or on arrival), never because the manager clicked the pill or the minimise chevron back open themselves — see toggleOpen. Since item 27 this no longer schedules anything; it only decides whether a new arrival is allowed to steer the active tab. */
  private autoOpened = this.open();

  /** Round 4, 1b — true from `pointerdown` until the *next* `pointerdown`, but only actually SET by `cdkDragStarted` (which CDK emits only once the 5px threshold is crossed) — so a plain click never sees it true, and a real drag suppresses the click its own mouseup synthesises. Pill and card each get their own; the card's covers the header, mute and minimise buttons together since all three sit inside the same `cdkDragHandle`. */
  private pillDragged = false;
  private cardDragged = false;

  /** Round 4, 1c — once the manager has dragged or resized the card themselves, a plain window resize must never recompute `topPx` back onto them; see the class doc comment. */
  private topPinned = false;

  private readonly onWindowResize = (): void => {
    if (this.topPinned) return;
    this.topPx.set(this.restingTopPx(this.heightPx()));
  };

  constructor() {
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize);
      this.destroyRef.onDestroy(() => window.removeEventListener('resize', this.onWindowResize));
    }

    // A new arrival (guest request, order needing approval, or staff task)
    // surfaces the dock automatically — a manager who collapsed it should
    // not have to remember to check back. Only an id this dock has never
    // seen counts: a listener re-emitting the same items, or an item being
    // cleared, never re-opens it.
    effect(() => {
      const licenseId = this.tenancy.licenseId();
      const showOrders = this.showTasksTab();
      const snapshot: Record<DockTab, { loaded: boolean; ids: readonly string[] }> = {
        orders: {
          loaded: !showOrders || this.ordersStore.pendingApprovalLoaded(),
          ids: showOrders ? this.ordersStore.pendingApproval().map((o) => o.id) : [],
        },
        requests: {
          loaded: this.pendingRequests.pendingLoaded(),
          ids: this.pendingRequests.pending().map((r) => r.id),
        },
        tasks: { loaded: this.tasksStore.loaded(), ids: this.pendingTasks().map((t) => t.id) },
      };
      untracked(() => this.noteArrivals(licenseId, snapshot));
    });
  }

  private noteArrivals(
    licenseId: string | null,
    snapshot: Record<DockTab, { loaded: boolean; ids: readonly string[] }>
  ): void {
    // A different venue (or signing out) starts a fresh baseline.
    if (licenseId !== this.arrivalsLicenseId) {
      this.arrivalsLicenseId = licenseId;
      this.seenIds.clear();
      this.baselined.clear();
    }

    let arrivedIn: DockTab | null = null;
    let backlogIn: DockTab | null = null;
    for (const tab of ARRIVAL_PRIORITY) {
      const { loaded, ids } = snapshot[tab];
      if (!loaded) continue;
      const firstSnapshot = !this.baselined.has(tab);
      this.baselined.add(tab);
      let fresh = false;
      for (const id of ids) {
        const key = `${tab}:${id}`;
        if (this.seenIds.has(key)) continue;
        this.seenIds.add(key);
        fresh = true;
      }
      if (!fresh) continue;
      if (firstSnapshot) backlogIn ??= tab;
      else arrivedIn ??= tab;
    }

    // The initial backlog may still surface a dock the manager hasn't
    // touched (a laptop-sized viewport starts as the pill), but never
    // overrides a minimise — that's reserved for genuinely new items.
    const surfaceIn = arrivedIn ?? (this.dismissedByUser ? null : backlogIn);
    if (!surfaceIn) return;
    // Only steer the tab when the card is (re)surfacing on its own, never
    // under a manager who opened it and is reading another tab.
    if (!this.open() || this.autoOpened) this.activeTab.set(surfaceIn);
    // Item 27 — surfacing on arrival is kept; it just no longer arms a
    // timer to put itself away again. The dock now stays up until the
    // manager minimises it (see the class doc comment for the tradeoff).
    if (!this.open()) {
      this.open.set(true);
      this.autoOpened = true;
    }
  }

  private defaultOpen(): boolean {
    if (typeof window === 'undefined') return false;
    return window.innerWidth >= DEFAULT_OPEN_MIN_WIDTH && window.innerHeight >= DEFAULT_OPEN_MIN_HEIGHT;
  }

  /** Same bottom-right inset the SCSS pill/card used to encode in a static `bottom` declaration — mirrored here in px so `topPx` can reproduce it, and narrowed the same way under `_breakpoints`'s mobile cutoff. */
  private restingBottomInset(): number {
    if (typeof window === 'undefined') return BOTTOM_INSET_PX;
    return window.innerWidth <= NARROW_MAX_WIDTH_PX ? BOTTOM_INSET_NARROW_PX : BOTTOM_INSET_PX;
  }

  private restingTopPx(heightPx: number): number {
    if (typeof window === 'undefined') return 0;
    return window.innerHeight - this.restingBottomInset() - heightPx;
  }

  setTab(tab: DockTab): void {
    this.activeTab.set(tab);
  }

  /** Concern 9 — the dock is a persistent panel, not a popup, so it deliberately doesn't swallow outside clicks the way a real dropdown does; Escape-to-minimise is the one piece of standard dismissal behaviour that still makes sense for it. */
  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open()) this.minimise();
  }

  /** The only way the card closes now (item 27): the chevron, Escape, or the pill toggling it back shut. */
  private minimise(): void {
    this.autoOpened = false;
    this.dismissedByUser = true;
    this.open.set(false);
  }

  /** Opening from the pill is a manual open — a manager who clicked to see what's there also owns the active tab, so a later arrival won't switch tabs under them (see the arrival effect's `autoOpened` check). */
  toggleOpen(): void {
    if (this.open()) {
      this.minimise();
      return;
    }
    this.autoOpened = false;
    this.open.set(true);
  }

  /** Round 4, 1b — resets the pill's drag flag at the start of every gesture; only a real drag (see onPillDragStarted) will make onPillClick ignore the click that follows. */
  onPillPointerDown(): void {
    this.pillDragged = false;
  }

  /** CDK only emits this once the pointer has actually crossed its own 5px drag threshold — never for a plain click. */
  onPillDragStarted(): void {
    this.pillDragged = true;
  }

  onPillClick(): void {
    if (this.pillDragged) return;
    this.toggleOpen();
  }

  /** Round 4, 1b/1c — same per-gesture reset as the pill, for the card's header/mute/minimise buttons; see onCardDragStarted for why this also pins the resize anchor. */
  onCardHeaderPointerDown(): void {
    this.cardDragged = false;
  }

  onCardDragStarted(): void {
    this.cardDragged = true;
    // A real drag is a deliberate reposition — from here on a plain window
    // resize must never recompute topPx back under the manager's hands.
    this.topPinned = true;
  }

  onSoundClick(): void {
    if (this.cardDragged) return;
    this.enableSound();
  }

  onMinimiseClick(): void {
    if (this.cardDragged) return;
    this.toggleOpen();
  }

  private enableSound(): void {
    void this.alarm.unlock();
  }

  onCardDragEnded(event: CdkDragEnd): void {
    this.cardPosition.set(event.source.getFreeDragPosition());
  }

  onPillDragEnded(event: CdkDragEnd): void {
    this.pillPosition.set(event.source.getFreeDragPosition());
  }

  /**
   * Bottom-right corner grip — pointer-based since CDK has no built-in
   * resize primitive. Mirrors the mock's own reqResizeStart clamp (180…
   * innerHeight-40) so the card can never grow taller than the viewport or
   * shrink to nothing.
   *
   * Round 4, 1c — only ever mutates heightPx, never topPx: the top edge
   * stays exactly where it is and the bottom edge (top + height) tracks the
   * pointer, so the card visibly grows downward from the handle instead of
   * upward from a pinned bottom. `top` is also marked pinned so a window
   * resize mid-session doesn't fight a resize the manager just made.
   *
   * The max-height ceiling uses RESIZE_BOTTOM_MARGIN_PX, not
   * `restingBottomInset()` — see that constant's own doc comment for why
   * reusing the resting margin here was a real, live-caught bug (it made
   * every resting height read as already-at-maximum, so the grip could
   * only ever shrink the card, never grow it, on any screen size).
   */
  startResize(event: PointerEvent): void {
    event.preventDefault();
    this.topPinned = true;
    const startY = event.clientY;
    const startHeight = this.heightPx();
    // The card's actual on-screen top, including any free-drag offset from
    // having been dragged elsewhere first — CDK's translate stacks on top
    // of the CSS/topPx anchor, so the clamp below has to account for both.
    const actualTop = this.topPx() + this.cardPosition().y;

    const onMove = (moveEvent: PointerEvent) => {
      const delta = moveEvent.clientY - startY;
      const max = Math.max(MIN_HEIGHT_PX, window.innerHeight - RESIZE_BOTTOM_MARGIN_PX - actualTop);
      this.heightPx.set(Math.min(max, Math.max(MIN_HEIGHT_PX, startHeight + delta)));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  }
}
